<template>
  <BContainer v-if="animal" class="container-pages">
    <BRow>
      <BCol xl="8">
        <div class="shadow rounded p-element-page bg-white">
          <h1 class="h1-title">{{ animal.description }}</h1>
          <hr />
        </div>

        <div v-if="$screen.showMobileAds" class="a-container mb-3">
          <OtherMobile />
        </div>

        <div class="shadow rounded p-element-page bg-white mt-2">
          <div v-html="animal.title" />
          <div class="mt-3" v-html="animal.names" />
        </div>

        <div v-if="$screen.isProduction" class="a-container mb-3">
          <OtherBottom />
        </div>
      </BCol>

      <BCol xl="4">
        <div v-if="$screen.showDesktopAds" class="a-container mb-3">
          <OtherTopRight />
        </div>

        <WidgetsAdvertisementTopAdsPages
          :items="item?.data ?? []"
          category_name=""
          :display_img="true"
        />
      </BCol>
    </BRow>
  </BContainer>

  <!-- opcioni fallback -->
  <BContainer v-else class="container-pages">
    <div class="shadow rounded p-element-page bg-white">
      <h1 class="h1-title">Stranica nije pronađena</h1>
    </div>
  </BContainer>
</template>

<script setup>
import { WidgetsAdvertisementTopAdsPages } from '#components'
import { BContainer, BRow, BCol } from 'bootstrap-vue-next'
import { useShareMetaStore } from "~/stores/shareMeta";
const shareMeta = useShareMetaStore();
import animals from '~/util/animal/animalName'
import OtherMobile from '~/components/GoogleAdvertisements/OtherMobile.vue'
import OtherBottom from '~/components/GoogleAdvertisements/OtherBottom.vue'
import OtherTopRight from '~/components/GoogleAdvertisements/OtherTopRight.vue'

import { useCustomerStore } from '~/stores/customer'

const useStore = useCustomerStore()
const config = useRuntimeConfig()
const isProdMode = computed(() => !!config.public.isProdMode)

const device = useDevice()
const route = useRoute()

const animalsList = animals?.animals ?? []

const animal = computed(() => {
  const param = String(route.params.animal ?? '')
    .toLowerCase()
    .split('-')
    .join(' ')
    .trim()

  const found = animalsList.find(
    (x) => String(x.description_path ?? '').toLowerCase().trim() === param
  )

  return found || null
})

const { data: item } = await useAsyncData(
  'item-imena-zivotinja-detalji',
  () => useStore.getCacheTopAd()
)

/* ---------------- SEO / HEAD ---------------- */

const canonicalUrl = computed(() => `${config.public.API_BASE}${route.fullPath}`)

const headTitle = computed(() => animal.value?.description ?? 'Imena životinja')

const headDescription = computed(() => {
  if (!animal.value) return 'Imena za životinje.'
  // animal.title je HTML, strip-uj za clean tekst (do 300 znakova)
  const clean = String(animal.value.title || '')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  return clean.length > 0 ? clean.slice(0, 300) : `Imena za ${animal.value.description}`
})

// JSON-LD: Article schema za imena životinja po vrsti
const articleLd = computed(() => {
  if (!animal.value) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${canonicalUrl.value}#article`,
    headline: animal.value.description,
    name: animal.value.description,
    description: headDescription.value,
    url: canonicalUrl.value,
    inLanguage: 'sr',
    author: {
      '@type': 'Organization',
      name: 'Pijace.com',
      url: 'https://pijace.com'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Pijace.com',
      url: 'https://pijace.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://pijace.com/images/pijace-60.png'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl.value
    },
    about: {
      '@type': 'Thing',
      name: `Imena za ${animal.value.description}`
    }
  }
})

// JSON-LD: BreadcrumbList — Imena životinja → vrsta
const breadcrumbLd = computed(() => {
  if (!animal.value) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Imena životinja',
        item: 'https://pijace.com/imena-zivotinja'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: animal.value.description,
        item: canonicalUrl.value
      }
    ]
  }
})

watchEffect(() => {
  if (!animal.value) return
  shareMeta.setMeta({
    title: headTitle.value,
    description: headDescription.value,
    url: canonicalUrl.value,
  })
})

usePijaceSeo({
  title: headTitle,
  description: headDescription,
  url: canonicalUrl,
  type: 'article',
  jsonLd: () => [articleLd.value, breadcrumbLd.value],
})

// Kad animal nije pronadjen — noindex
useHead(() => ({
  meta: animal.value
    ? []
    : [{ hid: 'robots', name: 'robots', content: 'noindex,follow' }]
}))
</script>
